'use client'

import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers'
import { arrayMove, horizontalListSortingStrategy, SortableContext } from '@dnd-kit/sortable'
import { Table, TableBody, TableCell, TableHeader, TableRow, useSidebar } from '@repo/ui/components'
import { cn } from '@repo/ui/lib'
import {
  ColumnDef,
  ColumnPinningState,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useState } from 'react'

import { DragAlongCell } from './cell.tsx'
import { DraggableTableHeader } from './header.tsx'

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  initialColumnPinning?: ColumnPinningState
  onRowClick?: (row: TData) => void
}

export function DndDataTable<TData, TValue>({
  columns,
  data,
  initialColumnPinning,
  onRowClick,
}: DataTableProps<TData, TValue>) {
  const [columnOrder, setColumnOrder] = useState<string[]>(() => columns.map(c => c.id!))
  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>(initialColumnPinning ?? {})

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(), //todo move to server side filtering
    getSortedRowModel: getSortedRowModel(),
    onColumnOrderChange: setColumnOrder,
    onColumnPinningChange: setColumnPinning,
    state: {
      columnOrder,
      columnPinning,
    },
  })

  // reorder columns after drag & drop
  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (active && over && active.id !== over.id) {
      setColumnOrder(columnOrder => {
        const oldIndex = columnOrder.indexOf(active.id as string)
        const newIndex = columnOrder.indexOf(over.id as string)
        return arrayMove(columnOrder, oldIndex, newIndex) //this is just a splice util
      })
    }
  }

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {}),
  )

  const { isMobile, state } = useSidebar()

  return (
    <DndContext
      collisionDetection={closestCenter}
      modifiers={[restrictToHorizontalAxis]}
      onDragEnd={handleDragEnd}
      sensors={sensors}
    >
      <div
        className={cn(
          'border',
          'overflow-auto',
          'max-h-[calc(100svh-168px)]',
          isMobile
            ? 'max-w-full'
            : state === 'collapsed'
              ? `max-w-[calc(100vw-150px)]`
              : `max-w-[calc(100vw-298px)]`,
        )}
      >
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map(headerGroup => (
              <TableRow key={headerGroup.id}>
                <SortableContext items={columnOrder} strategy={horizontalListSortingStrategy}>
                  {headerGroup.headers.map(header => (
                    <DraggableTableHeader header={header} key={header.id} />
                  ))}
                </SortableContext>
              </TableRow>
            ))}
          </TableHeader>
          <TableBody className={cn('overflow-y-auto')}>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map(row => (
                <TableRow
                  className={'bg-white'}
                  data-state={row.getIsSelected() && 'selected'}
                  key={row.id}
                  onClick={() => onRowClick?.(row.original)}
                >
                  {row.getVisibleCells().map(cell => (
                    <SortableContext
                      items={columnOrder}
                      key={cell.id}
                      strategy={horizontalListSortingStrategy}
                    >
                      <DragAlongCell cell={cell} key={cell.id} />
                    </SortableContext>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell className="h-24 text-center" colSpan={columns.length}>
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </DndContext>
  )
}
